<template>
  <div class="space-y-3">
    <p class="text-xs text-gray-500">All of these details are optional.</p>

    <!-- Thoughts Section -->
    <section class="group">
      <div class="flex items-center justify-between">
        <span class="text-xs font-medium text-gray-400 uppercase tracking-wide">
          Thoughts
        </span>
        <button
          type="button"
          class="text-xs text-gray-500 hover:text-white transition-colors"
          @click="openNoteSheet">
          <fa-icon :icon="note ? 'pen' : 'plus'" class="mr-1" />
          {{ note ? 'Edit' : 'Add' }}
        </button>
      </div>
      <div class="mt-1.5">
        <div
          v-if="note"
          class="text-sm text-gray-300 cursor-pointer text-pretty"
          @click="openNoteSheet">
          <fa-icon icon="quote-left" class="text-gray-500 mr-1.5 text-xs" />
          {{ note }}
        </div>
        <button
          v-else
          type="button"
          class="flex items-center gap-2 py-2 px-3 text-sm text-gray-500 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors w-full"
          @click="openNoteSheet">
          <fa-icon icon="quote-left" class="text-gray-600" />
          <span>Add your thoughts</span>
        </button>
      </div>
    </section>

    <!-- Sentiment Section -->
    <section class="group">
      <div class="flex items-center justify-between">
        <span class="text-xs font-medium text-gray-400 uppercase tracking-wide">
          Sentiment
        </span>
        <button
          type="button"
          class="text-xs text-gray-500 hover:text-white transition-colors"
          @click="openSentimentSheet">
          <fa-icon :icon="sentiment ? 'pen' : 'plus'" class="mr-1" />
          {{ sentiment ? 'Edit' : 'Add' }}
        </button>
      </div>
      <div class="mt-1.5">
        <div
          v-if="selectedSentimentOption"
          class="flex items-center gap-2 cursor-pointer"
          @click="openSentimentSheet">
          <img
            :src="selectedSentimentOption.path"
            alt=""
            class="size-10 rounded-full border border-black shadow-lg" />
          <span class="text-sm text-gray-300 italic">{{
            selectedSentimentOption.label
          }}</span>
        </div>
        <button
          v-else
          type="button"
          class="flex items-center gap-2 py-2 px-3 text-sm text-gray-500 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors"
          @click="openSentimentSheet">
          <fa-icon icon="face-smile" class="text-gray-600" />
          <span>How did you feel?</span>
        </button>
      </div>
    </section>

    <!-- Tags Section -->
    <section class="group">
      <div class="flex items-center justify-between">
        <span class="text-xs font-medium text-gray-400 uppercase tracking-wide">
          Tags
        </span>
        <button
          type="button"
          class="text-xs text-gray-500 hover:text-white transition-colors"
          @click="openTagsSheet">
          <fa-icon :icon="parsedTags.length > 0 ? 'pen' : 'plus'" class="mr-1" />
          {{ parsedTags.length > 0 ? 'Edit' : 'Add' }}
        </button>
      </div>
      <div class="mt-1.5">
        <div
          v-if="parsedTags.length > 0"
          class="flex flex-wrap gap-2 cursor-pointer"
          @click="openTagsSheet">
          <span
            v-for="(tagObj, index) in parsedTags"
            :key="index"
            :class="[
              'text-xs font-medium px-2.5 py-0.5 rounded-sm text-white',
              tagObj.color
            ]">
            {{ tagObj.label }}
          </span>
        </div>
        <button
          v-else
          type="button"
          class="flex items-center gap-2 py-2 px-3 text-sm text-gray-500 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors"
          @click="openTagsSheet">
          <fa-icon icon="tags" class="text-gray-600" />
          <span>Add some tags</span>
        </button>
      </div>
    </section>

    <!-- Watched On Section -->
    <section class="group">
      <div class="flex items-center justify-between">
        <span class="text-xs font-medium text-gray-400 uppercase tracking-wide">
          Watched On
        </span>
        <button
          type="button"
          class="text-xs text-gray-500 hover:text-white transition-colors"
          @click="openWatchedSourceSheet">
          <fa-icon :icon="hasWatchedOn ? 'pen' : 'plus'" class="mr-1" />
          {{ hasWatchedOn ? 'Edit' : 'Add' }}
        </button>
      </div>
      <div class="mt-1.5">
        <div
          v-if="hasWatchedOn"
          class="flex gap-2 items-center cursor-pointer"
          @click="openWatchedSourceSheet">
          <div
            class="flex gap-2 items-center py-1.5 px-3 text-sm text-gray-200 bg-gray-700 rounded-md">
            <fa-icon
              :icon="getWatchMethodIcon(watchedOnParsed.method)"
              class="text-blue-400" />
            <span v-if="watchedOnParsed.method">{{
              capitalizeFirst(watchedOnParsed.method)
            }}</span>
            <template v-if="watchedOnParsed.service">
              <span class="text-gray-400">-</span>
              <img
                v-if="watchedOnParsed.logoPath"
                :src="watchedOnParsed.logoPath"
                :alt="watchedOnParsed.service"
                class="size-4 object-contain" />
              <span>{{ watchedOnParsed.service }}</span>
            </template>
          </div>
        </div>
        <button
          v-else
          type="button"
          class="flex items-center gap-2 py-2 px-3 text-sm text-gray-500 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors"
          @click="openWatchedSourceSheet">
          <fa-icon icon="play" class="text-gray-600" />
          <span>Where did you watch?</span>
        </button>
      </div>
    </section>

    <!-- When Section -->
    <section class="group">
      <div class="flex items-center justify-between">
        <span class="text-xs font-medium text-gray-400 uppercase tracking-wide">
          When
        </span>
        <button
          type="button"
          class="text-xs text-gray-500 hover:text-white transition-colors"
          @click="openWhenSheet">
          <fa-icon :icon="when ? 'pen' : 'plus'" class="mr-1" />
          {{ when ? 'Edit' : 'Add' }}
        </button>
      </div>
      <div class="mt-1.5">
        <div
          v-if="when"
          class="inline-flex items-center gap-2 py-1.5 px-3 text-sm text-gray-200 bg-gray-700 rounded-md cursor-pointer"
          @click="openWhenSheet">
          <fa-icon icon="calendar" class="text-blue-400" />
          {{ formattedWhen }}
        </div>
        <button
          v-else
          type="button"
          class="flex items-center gap-2 py-2 px-3 text-sm text-gray-500 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors"
          @click="openWhenSheet">
          <fa-icon icon="calendar" class="text-gray-600" />
          <span>When did you watch?</span>
        </button>
      </div>
    </section>

    <!-- Highlight / Lowlight Section -->
    <section class="flex gap-4">
      <div class="flex-1">
        <div class="flex items-center justify-between">
          <span
            class="text-xs font-medium text-gray-400 uppercase tracking-wide">
            Highlight
          </span>
          <button
            type="button"
            class="text-xs text-gray-500 hover:text-white transition-colors"
            @click="openHighlightSheet">
            <fa-icon :icon="highlight ? 'pen' : 'plus'" class="mr-1" />
            {{ highlight ? 'Edit' : 'Add' }}
          </button>
        </div>
        <div class="mt-1.5">
          <div
            v-if="highlight"
            class="text-sm text-gray-400 cursor-pointer"
            @click="openHighlightSheet">
            <fa-icon icon="star" class="text-yellow-400 mr-1" />
            {{ capitalizeFirst(highlight) }}
          </div>
          <button
            v-else
            type="button"
            class="flex items-center gap-2 py-2 px-3 text-sm text-gray-500 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors w-full"
            @click="openHighlightSheet">
            <fa-icon icon="star" class="text-gray-600" />
            <span>Best part?</span>
          </button>
        </div>
      </div>
      <div class="flex-1">
        <div class="flex items-center justify-between">
          <span
            class="text-xs font-medium text-gray-400 uppercase tracking-wide">
            Lowlight
          </span>
          <button
            type="button"
            class="text-xs text-gray-500 hover:text-white transition-colors"
            @click="openLowlightSheet">
            <fa-icon :icon="lowlight ? 'pen' : 'plus'" class="mr-1" />
            {{ lowlight ? 'Edit' : 'Add' }}
          </button>
        </div>
        <div class="mt-1.5">
          <div
            v-if="lowlight"
            class="text-sm text-gray-400 cursor-pointer"
            @click="openLowlightSheet">
            <fa-icon icon="thumbs-down" class="text-red-400 mr-1" />
            {{ capitalizeFirst(lowlight) }}
          </div>
          <button
            v-else
            type="button"
            class="flex items-center gap-2 py-2 px-3 text-sm text-gray-500 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors w-full"
            @click="openLowlightSheet">
            <fa-icon icon="thumbs-down" class="text-gray-600" />
            <span>Worst part?</span>
          </button>
        </div>
      </div>
    </section>

    <!-- Club Section -->
    <section v-if="showClubSelector && hasClubsAccess" class="group">
      <div class="flex items-center justify-between">
        <span class="text-xs font-medium text-gray-400 uppercase tracking-wide">
          Share to Club
        </span>
        <button
          type="button"
          class="text-xs text-gray-500 hover:text-white transition-colors"
          @click="openClubSheet">
          <fa-icon :icon="clubId ? 'pen' : 'plus'" class="mr-1" />
          {{ clubId ? 'Edit' : 'Add' }}
        </button>
      </div>
      <div class="mt-1.5">
        <div
          v-if="clubId"
          class="inline-flex items-center gap-2 py-1.5 px-3 text-sm text-gray-200 bg-gray-700 rounded-md cursor-pointer"
          @click="openClubSheet">
          <fa-icon icon="users" class="text-blue-400" />
          Club selected
        </div>
        <button
          v-else
          type="button"
          class="flex items-center gap-2 py-2 px-3 text-sm text-gray-500 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors"
          @click="openClubSheet">
          <fa-icon icon="users" class="text-gray-600" />
          <span>Share to a club</span>
        </button>
      </div>
    </section>
  </div>

  <!-- Sentiment Edit Sheet -->
  <Sheet v-model="showSentimentSheet" title="Sentiment" size="md" content-class="px-4 pb-4">
    <p class="text-sm text-gray-400 mb-4">
      How did you feel about this overall?
    </p>
    <div class="flex justify-center gap-3">
      <button
        v-for="option in sentimentOptions"
        :key="option.value"
        type="button"
        :class="[
          'rounded-full transition-all duration-200',
          sentiment === option.value
            ? 'ring-4 ring-blue-500 scale-110'
            : 'opacity-60 hover:opacity-100'
        ]"
        @click="updateSentimentAndClose(option.value)">
        <img
          :src="option.path"
          alt=""
          class="size-12 rounded-full border border-black shadow-lg" />
      </button>
    </div>
  </Sheet>

  <!-- Tags Edit Sheet -->
  <Sheet v-model="showTagsSheet" title="Tags" size="md" content-class="px-4 pb-4">
    <p class="text-sm text-gray-400 mb-4">
      Add tags to help categorize this review
    </p>
    <!-- Current tags -->
    <div v-if="parsedTags.length > 0" class="flex flex-wrap gap-2 mb-4">
      <span
        v-for="(tagObj, index) in parsedTags"
        :key="index"
        :class="[
          'flex items-center gap-1 px-2 py-1 text-xs text-white rounded-md',
          tagObj.color
        ]">
        {{ tagObj.label }}
        <button
          type="button"
          class="text-white/80 hover:text-white"
          @click="removeTag(tagObj.label)">
          <fa-icon icon="xmark" class="text-xs" />
        </button>
      </span>
    </div>
    <!-- Tag input -->
    <input
      ref="tagInputRef"
      v-model="tagInput"
      type="text"
      placeholder="Type a tag and press space or enter"
      class="w-full p-3 text-sm text-gray-200 bg-gray-800 rounded-lg border border-gray-700 focus:ring-blue-500 focus:border-blue-500"
      @keydown="handleTagKeydown" />
  </Sheet>

  <!-- Watched Source Edit Sheet -->
  <Sheet v-model="showWatchedSourceSheet" title="Where did you watch?" size="lg" content-class="px-4 pb-4">
    <!-- Watch Method Selection -->
    <div class="mb-4">
      <label class="block mb-2 text-sm font-medium text-gray-300">
        How did you watch?
      </label>
      <div class="grid grid-cols-2 gap-2">
        <button
          v-for="method in watchMethods"
          :key="method.value"
          type="button"
          class="flex gap-2 justify-center items-center py-3 px-3 text-sm rounded-lg transition-colors"
          :class="
            tempWatchMethod === method.value
              ? 'bg-blue-600 text-white'
              : 'bg-gray-800 text-gray-200 hover:bg-gray-700'
          "
          @click="selectWatchMethodAndSave(method.value)">
          <fa-icon :icon="method.icon" />
          {{ method.label }}
        </button>
      </div>
      <!-- Clear option -->
      <button
        v-if="hasWatchedOn"
        type="button"
        class="flex w-full gap-2 justify-center items-center py-2 px-3 mt-2 text-sm text-gray-400 hover:text-white transition-colors"
        @click="clearWatchedSource">
        <fa-icon icon="xmark" />
        Clear selection
      </button>
    </div>

    <!-- Service Selection (for methods a service can accompany) -->
    <div
      v-if="tempWatchMethod && !servicelessMethods.includes(tempWatchMethod)"
      class="mb-4">
      <label class="block mb-2 text-sm font-medium text-gray-300">
        Service
      </label>
      <!-- Search input -->
      <div class="relative mb-3">
        <fa-icon
          icon="search"
          class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          ref="serviceSearchRef"
          v-model="serviceSearchQuery"
          type="text"
          placeholder="Search services..."
          class="w-full p-3 pl-10 text-sm text-gray-200 bg-gray-800 rounded-lg border border-gray-700 focus:ring-blue-500 focus:border-blue-500" />
        <button
          v-if="serviceSearchQuery"
          type="button"
          class="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-white transition-colors"
          @click="serviceSearchQuery = ''">
          <fa-icon icon="xmark" />
        </button>
      </div>
      <!-- Service buttons -->
      <div v-if="filteredServices.length > 0" class="flex flex-wrap gap-2 mb-3">
        <button
          v-for="service in filteredServices"
          :key="service.provider_id"
          type="button"
          class="flex gap-2 items-center py-2 px-3 text-xs rounded-lg transition-colors"
          :class="
            tempWatchService === service.provider_name
              ? 'bg-blue-600 text-white'
              : 'bg-gray-800 text-gray-200 hover:bg-gray-700'
          "
          @click="selectServiceAndSave(service)">
          <img
            v-if="service.logo_path"
            :src="service.logo_path"
            :alt="service.provider_name"
            class="size-4 object-contain" />
          {{ service.provider_name }}
        </button>
      </div>
      <!-- Custom service option when search has no results or user wants custom -->
      <div
        v-if="serviceSearchQuery.trim() && filteredServices.length === 0"
        class="mb-3">
        <button
          type="button"
          class="flex w-full gap-2 items-center justify-center py-3 px-4 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 transition-colors"
          @click="saveSearchAsCustomService">
          <fa-icon icon="plus" />
          Add "{{ serviceSearchQuery.trim() }}" as custom service
        </button>
      </div>
      <!-- Custom service input fallback -->
      <div v-if="!serviceSearchQuery.trim()" class="relative">
        <input
          v-model="tempWatchService"
          type="text"
          placeholder="Or enter a custom service name..."
          class="w-full p-3 pr-12 text-sm text-gray-200 bg-gray-800 rounded-lg border border-gray-700 focus:ring-blue-500 focus:border-blue-500"
          @keydown.enter="saveCustomService" />
        <button
          v-if="tempWatchService"
          type="button"
          class="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-indigo-400 hover:text-indigo-300 transition-colors"
          @click="saveCustomService">
          <fa-icon icon="check" />
        </button>
      </div>
    </div>
  </Sheet>

  <!-- When Edit Sheet -->
  <Sheet v-model="showWhenSheet" title="When did you watch?" size="md" content-class="px-4 pb-2">
    <DateQuickPicker
      :model-value="when"
      @update:model-value="$emit('update:when', $event)" />
    <template #footer>
      <button
        type="button"
        class="w-full py-3 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 transition-colors"
        @click="showWhenSheet = false">
        Done
      </button>
    </template>
  </Sheet>

  <!-- Thoughts Edit Sheet -->
  <Sheet v-model="showNoteSheet" title="Thoughts" size="md" content-class="px-4 pb-4">
    <p class="text-sm text-gray-400 mb-3">
      Your short take on it, published with the review.
    </p>
    <div class="relative">
      <textarea
        ref="noteInputRef"
        v-model="tempNote"
        rows="3"
        maxlength="280"
        placeholder="Add your thoughts..."
        class="w-full p-3 pr-12 text-sm text-gray-200 bg-gray-800 rounded-lg border border-gray-700 focus:ring-blue-500 focus:border-blue-500 resize-none"
        @keydown.enter.meta="saveNote"
        @keydown.enter.ctrl="saveNote" />
      <button
        type="button"
        class="absolute right-2 bottom-2 p-2 text-indigo-400 hover:text-indigo-300 transition-colors"
        @click="saveNote">
        <fa-icon icon="check" class="text-lg" />
      </button>
    </div>
    <div class="mt-1 text-right text-xs text-gray-500">
      {{ tempNote.length }} / 280
    </div>
  </Sheet>

  <!-- Highlight Edit Sheet -->
  <Sheet v-model="showHighlightSheet" title="Highlight" size="md" content-class="px-4 pb-4">
    <p class="text-sm text-gray-400 mb-3">
      What was especially good about this?
    </p>
    <div class="relative">
      <textarea
        ref="highlightInputRef"
        v-model="tempHighlight"
        rows="3"
        maxlength="120"
        placeholder="The best part was..."
        class="w-full p-3 pr-12 text-sm text-gray-200 bg-gray-800 rounded-lg border border-gray-700 focus:ring-blue-500 focus:border-blue-500 resize-none"
        @keydown.enter.meta="saveHighlight"
        @keydown.enter.ctrl="saveHighlight" />
      <button
        type="button"
        class="absolute right-2 bottom-2 p-2 text-indigo-400 hover:text-indigo-300 transition-colors"
        @click="saveHighlight">
        <fa-icon icon="check" class="text-lg" />
      </button>
    </div>
    <div class="mt-1 text-right text-xs text-gray-500">
      {{ tempHighlight.length }} / 120
    </div>
  </Sheet>

  <!-- Lowlight Edit Sheet -->
  <Sheet v-model="showLowlightSheet" title="Lowlight" size="md" content-class="px-4 pb-4">
    <p class="text-sm text-gray-400 mb-3">
      What was especially bad about this?
    </p>
    <div class="relative">
      <textarea
        ref="lowlightInputRef"
        v-model="tempLowlight"
        rows="3"
        maxlength="120"
        placeholder="The worst part was..."
        class="w-full p-3 pr-12 text-sm text-gray-200 bg-gray-800 rounded-lg border border-gray-700 focus:ring-blue-500 focus:border-blue-500 resize-none"
        @keydown.enter.meta="saveLowlight"
        @keydown.enter.ctrl="saveLowlight" />
      <button
        type="button"
        class="absolute right-2 bottom-2 p-2 text-indigo-400 hover:text-indigo-300 transition-colors"
        @click="saveLowlight">
        <fa-icon icon="check" class="text-lg" />
      </button>
    </div>
    <div class="mt-1 text-right text-xs text-gray-500">
      {{ tempLowlight.length }} / 120
    </div>
  </Sheet>

  <!-- Club Selector Sheet -->
  <Sheet v-model="showClubSheet" title="Share to Club" size="md" content-class="px-4 pb-4">
    <FormClubSelector v-model="localClubId" @update:model-value="saveClubAndClose" />
  </Sheet>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue'
import { Sheet } from '@/components/Options'
import FormClubSelector from '@/components/Reviews/FormClubSelector.vue'
import DateQuickPicker from '@/components/Pure/DateQuickPicker.vue'
import { useHapticFeedback } from '@/composables/useHapticFeedback'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreClubs } from '@/stores/storeClubs'

const props = defineProps({
  sentiment: Number,
  sentimentOptions: Array,
  tags: String,
  watchedOn: String,
  when: String,
  highlight: String,
  lowlight: String,
  note: String,
  clubId: [String, Number],
  title: Object,
  showClubSelector: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits([
  'update:sentiment',
  'update:tags',
  'update:watchedOn',
  'update:when',
  'update:highlight',
  'update:lowlight',
  'update:note',
  'update:clubId'
])

const haptics = useHapticFeedback()
const storeProfile = useStoreProfile()
const storeClubs = useStoreClubs()

// Check if clubs feature is enabled for the user
const clubsEnabled = computed(() => {
  const profile = storeProfile.getCurrentUserProfile
  return profile?.clubs_enabled === true
})

// Check if user has clubs and clubs are enabled
const hasClubsAccess = computed(() => {
  if (!clubsEnabled.value) return false
  const clubs = storeClubs.myClubs || []
  return clubs.length > 0
})

// Sheet visibility states
const showSentimentSheet = ref(false)
const showTagsSheet = ref(false)
const showWatchedSourceSheet = ref(false)
const showWhenSheet = ref(false)
const showHighlightSheet = ref(false)
const showLowlightSheet = ref(false)
const showNoteSheet = ref(false)
const showClubSheet = ref(false)

// Temp values for sheets
const tagInput = ref('')
const tempHighlight = ref('')
const tempLowlight = ref('')
const tempNote = ref('')
const tempWatchMethod = ref('')
const tempWatchService = ref('')
const tempServiceLogo = ref(null)
const localClubId = ref(props.clubId)
const serviceSearchQuery = ref('')

// Input refs for autofocus
const tagInputRef = ref(null)
const highlightInputRef = ref(null)
const lowlightInputRef = ref(null)
const noteInputRef = ref(null)
const serviceSearchRef = ref(null)

// Watch for prop changes
watch(
  () => props.clubId,
  (val) => {
    localClubId.value = val
  }
)

// Color options for random tag colors
const bgColorOptions = [
  'bg-red-500/80',
  'bg-blue-500/80',
  'bg-green-500/80',
  'bg-teal-500/80',
  'bg-indigo-500/80',
  'bg-purple-500/80',
  'bg-pink-500/80',
  'bg-yellow-500/80',
  'bg-orange-500/80'
]

function getRandomColorClass() {
  const randomIndex = Math.floor(Math.random() * bgColorOptions.length)
  return bgColorOptions[randomIndex]
}

// Parse tags into array with colors
const parsedTags = computed(() => {
  if (!props.tags) return []
  return props.tags
    .split(',')
    .map((tag) => ({
      label: tag.trim(),
      color: getRandomColorClass()
    }))
    .filter((t) => t.label.length > 0)
})

// Get selected sentiment option
const selectedSentimentOption = computed(() => {
  if (!props.sentimentOptions || props.sentiment === null) return null
  return props.sentimentOptions.find((o) => o.value === props.sentiment)
})

// Watch methods
const watchMethods = [
  { value: 'cinema', label: 'Cinema', icon: 'ticket' },
  { value: 'stream', label: 'Streaming', icon: 'play' },
  { value: 'rent', label: 'Rental', icon: 'clock' },
  { value: 'buy', label: 'Purchased', icon: 'shopping-cart' },
  { value: 'airplane', label: 'Airplane', icon: 'plane' }
]

// Methods a service name cannot accompany: a cinema is not a service, and a
// seat-back screen is not one either. These save immediately on selection.
const servicelessMethods = ['cinema', 'airplane']

// Default popular services (fallback when no TMDB data)
const defaultServices = [
  {
    provider_id: 8,
    provider_name: 'Netflix',
    logo_path: 'https://image.tmdb.org/t/p/w92/t2yyOv40HZeVlLjYsCsPHnWLk4W.jpg'
  },
  {
    provider_id: 9,
    provider_name: 'Amazon Prime',
    logo_path: 'https://image.tmdb.org/t/p/w92/68MNrwlkpF7WnmNPXLah69CR5cb.jpg'
  },
  {
    provider_id: 337,
    provider_name: 'Disney+',
    logo_path: 'https://image.tmdb.org/t/p/w92/7rwgEs15tFwyR9NPQ5vpzxTj19Q.jpg'
  },
  {
    provider_id: 2,
    provider_name: 'Apple TV',
    logo_path: 'https://image.tmdb.org/t/p/w92/peURlLlr8jggOwK53fJ5wdQl05y.jpg'
  },
  {
    provider_id: 384,
    provider_name: 'HBO Max',
    logo_path: 'https://image.tmdb.org/t/p/w92/aS2zvJWn9mwiCOeaaCkIh4wleZS.jpg'
  },
  {
    provider_id: 15,
    provider_name: 'Hulu',
    logo_path: 'https://image.tmdb.org/t/p/w92/giwM8XX4V2AQb9vsoN7yti82tKK.jpg'
  }
]

// Extract all unique providers from the title's TMDB watch provider data
const allServices = computed(() => {
  const providers = props.title?.['watch/providers']?.results
  if (!providers) return []

  const services = new Map()
  Object.values(providers).forEach((region) => {
    ;['flatrate', 'rent', 'buy'].forEach((type) => {
      if (region[type]) {
        region[type].forEach((provider) => {
          if (!services.has(provider.provider_id)) {
            services.set(provider.provider_id, {
              ...provider,
              logo_path: provider.logo_path
                ? `https://image.tmdb.org/t/p/w92${provider.logo_path}`
                : null
            })
          }
        })
      }
    })
  })
  return Array.from(services.values())
})

// Use TMDB providers if available, otherwise fall back to defaults
const availableServices = computed(() => {
  return allServices.value.length > 0 ? allServices.value : defaultServices
})

// Filtered services based on search query
const filteredServices = computed(() => {
  if (!serviceSearchQuery.value.trim()) {
    return availableServices.value
  }
  const query = serviceSearchQuery.value.toLowerCase().trim()
  return availableServices.value.filter((service) =>
    service.provider_name.toLowerCase().includes(query)
  )
})

// Parse watched on value
const watchedOnParsed = computed(() => {
  if (!props.watchedOn) return {}
  try {
    return JSON.parse(props.watchedOn)
  } catch {
    return { service: props.watchedOn }
  }
})

const hasWatchedOn = computed(() => {
  return watchedOnParsed.value.method || watchedOnParsed.value.service
})

function getWatchMethodIcon(method) {
  if (!method) return 'play'
  const methodLower = method.toLowerCase()
  if (methodLower.includes('cinema') || methodLower.includes('theater')) {
    return 'ticket'
  } else if (methodLower.includes('stream')) {
    return 'play'
  } else if (methodLower.includes('rent')) {
    return 'clock'
  } else if (methodLower.includes('buy') || methodLower.includes('purchase')) {
    return 'shopping-cart'
  } else if (
    methodLower.includes('airplane') ||
    methodLower.includes('plane') ||
    methodLower.includes('flight')
  ) {
    return 'plane'
  }
  return 'play'
}

function capitalizeFirst(text) {
  if (!text) return ''
  return text.charAt(0).toUpperCase() + text.slice(1)
}

// Sheet openers
function openSentimentSheet() {
  haptics.light()
  showSentimentSheet.value = true
}

function openTagsSheet() {
  haptics.light()
  tagInput.value = ''
  showTagsSheet.value = true
  nextTick(() => {
    tagInputRef.value?.focus()
  })
}

function openWatchedSourceSheet() {
  haptics.light()
  // Initialize temp values from current
  tempWatchMethod.value = watchedOnParsed.value.method || ''
  tempWatchService.value = watchedOnParsed.value.service || ''
  tempServiceLogo.value = watchedOnParsed.value.logoPath || null
  serviceSearchQuery.value = ''
  showWatchedSourceSheet.value = true
}

// Format the when date for display
const formattedWhen = computed(() => {
  if (!props.when) return null
  try {
    const date = new Date(props.when + 'T12:00:00')
    const today = new Date()
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    if (date.toDateString() === today.toDateString()) return 'Today'
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    })
  } catch {
    return props.when
  }
})

function openWhenSheet() {
  haptics.light()
  showWhenSheet.value = true
}

function openHighlightSheet() {
  haptics.light()
  tempHighlight.value = props.highlight || ''
  showHighlightSheet.value = true
  nextTick(() => {
    highlightInputRef.value?.focus()
  })
}

function openNoteSheet() {
  haptics.light()
  tempNote.value = props.note || ''
  showNoteSheet.value = true
  nextTick(() => {
    noteInputRef.value?.focus()
  })
}

function openLowlightSheet() {
  haptics.light()
  tempLowlight.value = props.lowlight || ''
  showLowlightSheet.value = true
  nextTick(() => {
    lowlightInputRef.value?.focus()
  })
}

function openClubSheet() {
  haptics.light()
  localClubId.value = props.clubId
  showClubSheet.value = true
}

// Update handlers
function updateSentiment(value) {
  haptics.light()
  emit('update:sentiment', value)
}

function updateSentimentAndClose(value) {
  haptics.light()
  emit('update:sentiment', value)
  showSentimentSheet.value = false
}

function handleTagKeydown(event) {
  if (event.key === ' ' || event.key === 'Enter') {
    event.preventDefault()
    if (tagInput.value.trim()) {
      const currentTags = parsedTags.value.map((t) => t.label)
      if (!currentTags.includes(tagInput.value.trim())) {
        const newTags = [...currentTags, tagInput.value.trim()].join(', ')
        emit('update:tags', newTags)
      }
      tagInput.value = ''
    }
  } else if (
    event.key === 'Backspace' &&
    tagInput.value === '' &&
    parsedTags.value.length > 0
  ) {
    const currentTags = parsedTags.value.map((t) => t.label)
    currentTags.pop()
    emit('update:tags', currentTags.join(', '))
  }
}

function removeTag(label) {
  haptics.light()
  const newTags = parsedTags.value
    .filter((t) => t.label !== label)
    .map((t) => t.label)
    .join(', ')
  emit('update:tags', newTags)
}

function selectService(service) {
  haptics.light()
  tempWatchService.value = service.provider_name
  tempServiceLogo.value = service.logo_path
}

function selectWatchMethodAndSave(method) {
  haptics.light()
  tempWatchMethod.value = method
  // A serviceless method has nothing left to pick: save immediately and close
  if (servicelessMethods.includes(method)) {
    const value = {
      method: method,
      service: '',
      logoPath: null
    }
    emit('update:watchedOn', JSON.stringify(value))
    showWatchedSourceSheet.value = false
  }
  // For other methods, wait for service selection
}

function selectServiceAndSave(service) {
  haptics.medium()
  tempWatchService.value = service.provider_name
  tempServiceLogo.value = service.logo_path
  const value = {
    method: tempWatchMethod.value,
    service: service.provider_name,
    logoPath: service.logo_path
  }
  emit('update:watchedOn', JSON.stringify(value))
  showWatchedSourceSheet.value = false
}

function saveCustomService() {
  if (!tempWatchService.value.trim()) return
  haptics.medium()
  const value = {
    method: tempWatchMethod.value,
    service: tempWatchService.value.trim(),
    logoPath: null
  }
  emit('update:watchedOn', JSON.stringify(value))
  showWatchedSourceSheet.value = false
}

function saveSearchAsCustomService() {
  if (!serviceSearchQuery.value.trim()) return
  haptics.medium()
  const value = {
    method: tempWatchMethod.value,
    service: serviceSearchQuery.value.trim(),
    logoPath: null
  }
  emit('update:watchedOn', JSON.stringify(value))
  showWatchedSourceSheet.value = false
}

function saveWatchedSource() {
  haptics.medium()
  const value = {
    method: tempWatchMethod.value,
    service: tempWatchService.value,
    logoPath: tempServiceLogo.value
  }
  emit('update:watchedOn', JSON.stringify(value))
  showWatchedSourceSheet.value = false
}

function clearWatchedSource() {
  haptics.light()
  tempWatchMethod.value = ''
  tempWatchService.value = ''
  tempServiceLogo.value = null
  emit('update:watchedOn', '')
  showWatchedSourceSheet.value = false
}

function saveHighlight() {
  haptics.medium()
  emit('update:highlight', tempHighlight.value)
  showHighlightSheet.value = false
}

function saveNote() {
  haptics.medium()
  emit('update:note', tempNote.value.trim())
  showNoteSheet.value = false
}

function saveLowlight() {
  haptics.medium()
  emit('update:lowlight', tempLowlight.value)
  showLowlightSheet.value = false
}

function saveClubAndClose(value) {
  haptics.light()
  emit('update:clubId', value)
  showClubSheet.value = false
}
</script>
